import type {
  CompanyId,
  FieldId,
  ProjectDetail,
  ProjectStateUpdate,
  ProjectType,
} from '@core/core/types'
import { addProjectStateChangedNotification, addSimpleNotification } from '@notifications/actions'
import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import type { Nullable, ProjectId } from 'common/types'

import {
  changeProjectStateApi,
  createProjectApi,
  getAllProjectsApi,
  getProjectByIdApi,
  getProjectSizeApi,
  removeProjectApi,
} from '../../api'
import { setProject } from '../project'
import type { AppDispatch, AppThunk } from '../store'
import { projectsSlice } from './projectsSlice'

const { projectRemoved } = projectsSlice.actions

type SkipSizeRequestArg = {
  skipSizeRequest: boolean
}

export const getAllProjects = createAsyncThunk<ProjectDetail[], SkipSizeRequestArg | undefined>(
  'Project',
  async (args = { skipSizeRequest: false }, { dispatch }) => {
    const projects = await getAllProjectsApi()

    if (!args.skipSizeRequest) {
      dispatch(getAllProjectSizes(projects.map(({ id }) => id)))
    }

    return projects
  },
)

export const getAllProjectSizes = createAsyncThunk<
  Pick<ProjectDetail, 'id' | 'size'>[],
  ProjectId[]
>('Project/getProjectSizes', async projectIds => {
  const projectSizesPromises: Promise<number>[] = projectIds.map(getProjectSizeApi)

  return (await Promise.allSettled(projectSizesPromises)).map((settledResult, idx) => ({
    id: projectIds[idx],
    size: settledResult.status === 'fulfilled' ? settledResult.value.toPrecision(2) : '⚠',
  }))
})

export const createNewProject =
  (
    name: string,
    type: ProjectType,
    projectYear: number,
    companyId?: Nullable<CompanyId>,
    fieldId?: Nullable<FieldId>,
  ): AppThunk =>
  async dispatch => {
    try {
      await createProjectApi(name, type, projectYear, companyId, fieldId)
      dispatch(getAllProjects())
    } catch (ex) {
      console.log(ex)
    }
  }

export const changeProjectState = createAsyncThunk<
  ProjectStateUpdate,
  ProjectStateUpdate,
  { dispatch: AppDispatch }
>('ProjectState', async ({ projectId, newState }, { rejectWithValue }) => {
  const failMessage = 'Не удалось опубликовать проект'

  try {
    const result = await changeProjectStateApi(projectId, newState)
    if (result) {
      addProjectStateChangedNotification(newState)
      return { projectId, newState }
    }
  } catch {
    addSimpleNotification({ message: failMessage })
  }
  return rejectWithValue(failMessage)
})

export const removeProject =
  (projectId: ProjectId): AppThunk =>
  dispatch => {
    removeProjectApi(projectId)
      .then((successful: boolean) => {
        if (successful) {
          dispatch(projectRemoved([projectId]))
        }
      })
      .catch(console.error)
  }

export const getProjectById = createAsyncThunk<ProjectDetail, ProjectId, { dispatch: AppDispatch }>(
  'projectById',
  async (projectId, { dispatch, rejectWithValue }) => {
    try {
      const project = await getProjectByIdApi(projectId)
      dispatch(setProject(project.id))

      return project
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error)
    }
  },
)
